import React, { useEffect, useMemo, useRef, useState } from 'react';
import 'src/assets/styles/react-table.scss';
import { TPEReactTable } from 'src/components/shared/TPEReactTable';
import CONSTANTS from 'src/utils/constants';
import { ACTION_TYPE, TPEBasicModal } from 'src/components/shared/TPEBasicModal';
import { duplicateRecord, editRecord } from 'src/components/tp-allocation/shared/DatasetGridCommon';
import DataKeyModal from 'src/components/shared/DataKeyModal';
import { DatasetContext } from 'src/components/tp-allocation/shared/DatasetContainer';
import { ACTIONS } from 'src/services/tp-allocation/data-set/DatasetReducer';
import { DatasetRecord } from 'src/models/tp-allocation/DatasetRecord';
import { DataSourceRecord } from 'src/models/common/DataSourceRecord';
import { CustomPeriodModal } from 'src/components/calculation-builder/data-sources/CustomPeriodModal';
import ErrorUtils from 'src/utils/ErrorUtils';
import { DerivedAllocationSheetBuilderContext } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderContext';
import { ACTIONS as DAS_ACTIONS } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderReducer';
import { DasDataSetRow, dasDataSetCellEditor } from './DasDataSetCells';
import { getDasDataSetColumns } from './DasDataSetColumns';

const toDataSourceRecord = (x: DatasetRecord) => ({
    currency: x.currency, customPeriod: x.customPeriod, dataKey: x.dataKey, datasource: x.datasource,
    datasourceId: x.datasourceId, description: x.description, fxDate: x.fxDate, fxRate: x.fxRate, fxType: x.fxType,
    period: x.period, value: x.value, dataKeyInput: x.dataKeyInput,
} as DataSourceRecord);

/** FX rates always show decimals; a blank or non-numeric rate shows a dash. */
export const formatFxRate = (fxRate: string | undefined, format: (v: string) => string) =>
    (fxRate == null || fxRate === '' || Number.isNaN(Number(fxRate)) ? '-' : format(fxRate));

/**
 * Step 1 grid of the derived allocation sheet builder, adapted from the TPA dataset grid. Rows are deleted
 * through the derived allocation sheet service. Row menus are greyed out while a save (`saving`) or a delete
 * is in flight.
 */
export default function DasDataSetGrid(props: { onUpdateValue?: (record: DatasetRecord) => void, saving?: boolean }) {
    const { onUpdateValue, saving = false } = props;
    const { dasState, dasDispatch, sheetId } = React.useContext(DerivedAllocationSheetBuilderContext);
    const { services, datasetState, datasetDispatch } = React.useContext(DatasetContext);
    const { datasetRecords, recordBeingEdited, recordIdForAction, showDecimals, showEditActionConfirmModal,
        showDuplicateActionConfirmModal, showDeleteActionConfirmModal, savedRecordId, tpLOV } = datasetState;
    const { viewMode } = dasState;
    const [formattedRecords, setFormattedRecords] = useState<DasDataSetRow[]>([]);
    const [showCustomPeriodModal, setShowCustomPeriodModal] = useState(false);
    const [showDataKeyModal, setShowDataKeyModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const onRecordUpdated = (rowIndex: number, columnId: string, value?: string) => {
        if (columnId != CONSTANTS.DATA_SOURCE_FIELDS.DATA_KEY.ACCESSOR) {
            datasetDispatch(ACTIONS.UPDATE_RECORD_FIELD.withPayload({ rowIndex, fieldName: columnId, value }));
        } else if (value != 'view') {
            datasetDispatch(ACTIONS.SHOW_DATA_KEY_SELECTION);
        } else if (datasetRecords.length > rowIndex) {
            datasetDispatch(ACTIONS.TOGGLE_DATA_KEY_VIEW.withPayload({ recordID: datasetRecords[rowIndex].datasourceId }));
        }
    };

    const onPeriodUpdated = (rowIndex: number, columnId: string, value?: string) => {
        if (columnId == CONSTANTS.DATA_SOURCE_FIELDS.PERIOD.ACCESSOR && value == CONSTANTS.DATA_SOURCE_VALUES.CUSTOM_PERIOD) {
            setShowCustomPeriodModal(true);
        } else {
            datasetDispatch(ACTIONS.UPDATE_RECORD_FIELD.withPayload({ rowIndex, fieldName: columnId, value }));
        }
    };

    useEffect(() => {
        if (datasetRecords == null) {
            return;
        }
        const records = recordBeingEdited == undefined
            ? datasetRecords
            : datasetRecords.slice(0, datasetRecords.indexOf(recordBeingEdited) + 1);
        setFormattedRecords(records.map((x, index) => ({
            ...x,
            sequence: index + 1,
            fxRateFormatted: formatFxRate(x.fxRate, (v) => services.formattingService.formatString(v, true)),
            valueFormatted: x.value == null ? '-' : services.formattingService.formatString(String(x.value), showDecimals),
            showSavedIndicator: savedRecordId == x.datasourceId,
            enableDataKeyView: recordBeingEdited == null || !recordBeingEdited.isEditing,
        })));
    }, [datasetRecords, showDecimals, viewMode]);

    useEffect(() => {
        if (viewMode != CONSTANTS.VIEW_MODE.EDITABLE) {
            datasetDispatch(ACTIONS.CANCEL_EDITING);
        }
    }, [viewMode]);

    const setCustomPeriod = (customPeriodStart: string, customPeriodEnd: string) => {
        setShowCustomPeriodModal(false);
        if (recordBeingEdited != null) {
            datasetDispatch(ACTIONS.UPDATE_RECORD_FIELD.withPayload({ rowIndex: recordBeingEdited.datasourceId,
                fieldName: CONSTANTS.DATA_SOURCE_FIELDS.CUSTOM_PERIOD.ACCESSOR, value: `${customPeriodStart} - ${customPeriodEnd}` }));
        }
    };

    const processRecord = () => {
        if (showEditActionConfirmModal) {
            datasetDispatch(ACTIONS.HIDE_EDIT_CONFIRM_MODAL);
            editRecord(datasetDispatch, recordBeingEdited);
        } else if (showDuplicateActionConfirmModal) {
            datasetDispatch(ACTIONS.HIDE_DUPLICATE_CONFIRM_MODAL);
            duplicateRecord(datasetDispatch, recordBeingEdited);
        }
    };

    const deleteRecordConfirmed = async () => {
        datasetDispatch(ACTIONS.HIDE_DELETE_CONFIRM_MODAL);
        if (recordBeingEdited != null && recordBeingEdited.datasourceId == recordIdForAction && recordBeingEdited.isNewRecord) {
            datasetDispatch(ACTIONS.DELETE_RECORD);
            return;
        }
        setIsDeleting(true);
        try {
            await services.derivedAllocationSheetService.deleteDASDataSource({ sheetId, dataSourceId: recordIdForAction });
            if (!mountedRef.current) return;
            datasetDispatch(ACTIONS.DELETE_RECORD);
            dasDispatch(DAS_ACTIONS.REFRESH_SHEET);
        } catch (ex) {
            if (mountedRef.current) services.messageService.showErrorBanner(ErrorUtils.getMessage(ex));
        } finally {
            if (mountedRef.current) setIsDeleting(false);
        }
    };

    const rowMenuDisabled = saving || isDeleting;

    const columnDefinitions = useMemo(() => getDasDataSetColumns({
        viewMode, showDecimals, recordBeingEdited, datasetDispatch, onUpdateValue, rowMenuDisabled,
        onViewAllDataKeys: () => setShowDataKeyModal(true),
    }), [showDecimals, recordBeingEdited, viewMode, onUpdateValue, datasetDispatch, rowMenuDisabled]);

    return (
        <div className="dataSourceTableContainer">
            <TPEReactTable {...{
                data: formattedRecords,
                columnDefinitions,
                className: "dataSources",
                sortable: false,
                emptyMessage: "No records found",
                editableOptions: {
                    defaultColumn: dasDataSetCellEditor,
                    onRowUpdated: onRecordUpdated,
                    onCellUpdated: onPeriodUpdated,
                    editorsData: tpLOV,
                    skipPageReset: true,
                }
            }} />
            <TPEBasicModal
                className="dataSourceStopEditConfirmModal"
                visible={showEditActionConfirmModal || showDuplicateActionConfirmModal}
                action={ACTION_TYPE.YES_NO}
                title="Cancel editing"
                onCancel={() => { datasetDispatch(ACTIONS.HIDE_EDIT_CONFIRM_MODAL); datasetDispatch(ACTIONS.HIDE_DUPLICATE_CONFIRM_MODAL); }}
                onConfirm={processRecord}
            >
                Are you sure you want to stop editing your current dataset item?
            </TPEBasicModal>
            <TPEBasicModal
                className="dataSourceDeleteConfirmModal"
                visible={showDeleteActionConfirmModal}
                action={ACTION_TYPE.YES_NO}
                title="Delete dataset item"
                onCancel={() => datasetDispatch(ACTIONS.HIDE_DELETE_CONFIRM_MODAL)}
                onConfirm={deleteRecordConfirmed}
            >
                Are you sure you want to delete this dataset item?
            </TPEBasicModal>
            <CustomPeriodModal
                visible={showCustomPeriodModal}
                onClose={() => setShowCustomPeriodModal(false)}
                onSubmit={setCustomPeriod}
                recordBeingEdited={recordBeingEdited || {} as DatasetRecord}
            />
            <DataKeyModal
                visible={showDataKeyModal}
                onClose={() => setShowDataKeyModal(false)}
                dataSourceRecords={datasetRecords.map(toDataSourceRecord)}
                recordBeingEdited={{} as DataSourceRecord}
                dataSourcesAreEditable={false}
                showGLOnly={true} />
        </div>
    );
}
